import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { doc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';

const ProctoringContext = createContext();

export function useProctoring() {
  return useContext(ProctoringContext);
}

export function ProctoringProvider({ children }) {
  const { currentUser } = useRealtime();
  const [isProctoringActive, setIsProctoringActive] = useState(false);
  const [proctoringSession, setProctoringSession] = useState(null);
  const [violations, setViolations] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [multipleFacesDetected, setMultipleFacesDetected] = useState(false);
  const [screenRecording, setScreenRecording] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [videoStream, setVideoStream] = useState(null);
  const [behavioralData, setBehavioralData] = useState({
    mouseMovements: [],
    keyStrokes: [],
    tabSwitches: 0,
    copyPasteAttempts: 0,
    fullscreenExits: 0,
    suspiciousActivities: []
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const faceDetectionInterval = useRef(null);
  const behavioralTrackingInterval = useRef(null);

  // Initialize proctoring session
  const initializeProctoring = useCallback(async (testId, testInfo) => {
    if (!currentUser) return false;

    try {
      const sessionId = `proctoring_${testId}_${currentUser.uid}_${Date.now()}`;
      const sessionData = {
        sessionId,
        userId: currentUser.uid,
        testId,
        startTime: new Date(),
        status: 'active',
        violations: [],
        behavioralData: {},
        recordings: {
          video: null,
          screen: null,
          audio: null
        },
        settings: {
          faceDetection: true,
          screenRecording: true,
          audioMonitoring: true,
          behavioralTracking: true,
          browserSecurity: true
        }
      };

      // Create proctoring session in Firestore
      await setDoc(doc(db, 'proctoringSessions', sessionId), sessionData);

      setProctoringSession(sessionData);
      setIsProctoringActive(true);

      // Start all proctoring features
      await startBrowserSecurity();
      await startFaceDetection();
      await startScreenRecording();
      await startAudioMonitoring();
      await startBehavioralTracking();

      return true;
    } catch (error) {
      // Failed to initialize proctoring
      return false;
    }
  }, [currentUser]);

  // Browser Security Features
  const startBrowserSecurity = useCallback(() => {
    // Prevent tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        addViolation('TAB_SWITCH', 'User switched to another tab');
      }
    };

    // Prevent copy-paste
    const handleCopyPaste = (e) => {
      if (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'x')) {
        e.preventDefault();
        addViolation('COPY_PASTE', 'Copy-paste attempt detected');
      }
    };

    // Prevent right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      addViolation('RIGHT_CLICK', 'Right-click attempt detected');
    };

    // Prevent F11 and other shortcuts
    const handleKeyDown = (e) => {
      if (e.key === 'F11' || (e.ctrlKey && e.key === 'f')) {
        e.preventDefault();
        addViolation('KEYBOARD_SHORTCUT', 'Prohibited keyboard shortcut used');
      }
    };

    // Prevent leaving fullscreen
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        addViolation('FULLSCREEN_EXIT', 'User exited fullscreen mode');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleCopyPaste);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Force fullscreen
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {
        addViolation('FULLSCREEN_FAILED', 'Failed to enter fullscreen mode');
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleCopyPaste);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Face Detection using WebRTC
  const startFaceDetection = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: 640, 
          height: 480,
          facingMode: 'user'
        } 
      });
      
      setVideoStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Start face detection analysis
      faceDetectionInterval.current = setInterval(() => {
        analyzeFaceDetection();
      }, 1000);

    } catch (error) {
      // Failed to start face detection
      addViolation('FACE_DETECTION_FAILED', 'Failed to access camera');
    }
  }, []);

  // Face detection analysis using Canvas API
  const analyzeFaceDetection = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Simple face detection using skin tone detection
    // In production, use a proper face detection library like face-api.js
    const skinTonePixels = detectSkinTones(imageData);
    const faceDetected = skinTonePixels > (canvas.width * canvas.height * 0.1);
    
    setFaceDetected(faceDetected);
    
    if (!faceDetected) {
      addViolation('FACE_NOT_DETECTED', 'No face detected in camera');
    }
  }, []);

  // Simple skin tone detection (basic implementation)
  const detectSkinTones = (imageData) => {
    let skinTonePixels = 0;
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Basic skin tone detection
      if (r > 95 && g > 40 && b > 20 && 
          Math.max(r, g, b) - Math.min(r, g, b) > 15 &&
          Math.abs(r - g) > 15 && r > g && r > b) {
        skinTonePixels++;
      }
    }
    
    return skinTonePixels;
  };

  // Screen Recording
  const startScreenRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: { 
          mediaSource: 'screen',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      setScreenRecording(stream);
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        // Upload to Firebase Storage
        uploadRecording(blob, 'screen');
      };
      
      mediaRecorder.start(10000); // Record in 10-second chunks
      setIsRecording(true);
      
    } catch (error) {
      // Failed to start screen recording
      addViolation('SCREEN_RECORDING_FAILED', 'Failed to start screen recording');
    }
  }, []);

  // Audio Monitoring
  const startAudioMonitoring = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      setAudioStream(stream);
      
      // Audio analysis for suspicious sounds
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      
      source.connect(analyser);
      analyser.fftSize = 256;
      
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const analyzeAudio = () => {
        analyser.getByteFrequencyData(dataArray);
        
        // Detect sudden volume changes or suspicious patterns
        const averageVolume = dataArray.reduce((a, b) => a + b) / bufferLength;
        
        if (averageVolume > 200) { // High volume threshold
          addViolation('HIGH_AUDIO_LEVEL', 'Unusually high audio level detected');
        }
      };
      
      setInterval(analyzeAudio, 1000);
      
    } catch (error) {
      // Failed to start audio monitoring
      addViolation('AUDIO_MONITORING_FAILED', 'Failed to access microphone');
    }
  }, []);

  // Behavioral Tracking
  const startBehavioralTracking = useCallback(() => {
    let mouseMovements = [];
    let keyStrokes = [];
    
    // Track mouse movements
    const handleMouseMove = (e) => {
      mouseMovements.push({
        x: e.clientX,
        y: e.clientY,
        timestamp: Date.now()
      });
      
      // Keep only last 100 movements
      if (mouseMovements.length > 100) {
        mouseMovements = mouseMovements.slice(-100);
      }
    };
    
    // Track keystrokes
    const handleKeyPress = (e) => {
      keyStrokes.push({
        key: e.key,
        timestamp: Date.now()
      });
      
      // Keep only last 50 keystrokes
      if (keyStrokes.length > 50) {
        keyStrokes = keyStrokes.slice(-50);
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keypress', handleKeyPress);
    
    // Analyze behavioral patterns every 30 seconds
    behavioralTrackingInterval.current = setInterval(() => {
      analyzeBehavioralPatterns(mouseMovements, keyStrokes);
      setBehavioralData(prev => ({
        ...prev,
        mouseMovements: [...mouseMovements],
        keyStrokes: [...keyStrokes]
      }));
    }, 30000);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, []);

  // Analyze behavioral patterns for suspicious activity
  const analyzeBehavioralPatterns = useCallback((mouseMovements, keyStrokes) => {
    // Detect rapid mouse movements (potential cheating)
    if (mouseMovements.length > 10) {
      const recentMovements = mouseMovements.slice(-10);
      const rapidMovements = recentMovements.filter((movement, index) => {
        if (index === 0) return false;
        const prevMovement = recentMovements[index - 1];
        const distance = Math.sqrt(
          Math.pow(movement.x - prevMovement.x, 2) + 
          Math.pow(movement.y - prevMovement.y, 2)
        );
        const timeDiff = movement.timestamp - prevMovement.timestamp;
        return distance > 100 && timeDiff < 100; // Rapid large movements
      });
      
      if (rapidMovements.length > 3) {
        addViolation('RAPID_MOUSE_MOVEMENTS', 'Suspicious rapid mouse movements detected');
      }
    }
    
    // Detect unusual typing patterns
    if (keyStrokes.length > 20) {
      const recentKeys = keyStrokes.slice(-20);
      const typingSpeed = recentKeys.map((key, index) => {
        if (index === 0) return 0;
        return key.timestamp - recentKeys[index - 1].timestamp;
      }).filter(speed => speed > 0);
      
      const avgTypingSpeed = typingSpeed.reduce((a, b) => a + b, 0) / typingSpeed.length;
      
      if (avgTypingSpeed < 50) { // Very fast typing
        addViolation('UNUSUAL_TYPING_SPEED', 'Unusually fast typing detected');
      }
    }
  }, []);

  // Add violation to session
  const addViolation = useCallback((type, description) => {
    const violation = {
      type,
      description,
      timestamp: new Date(),
      severity: getViolationSeverity(type)
    };
    
    setViolations(prev => [...prev, violation]);
    
    // Update session in Firestore
    if (proctoringSession) {
      updateDoc(doc(db, 'proctoringSessions', proctoringSession.sessionId), {
        violations: [...violations, violation]
      });
    }
  }, [proctoringSession, violations]);

  // Get violation severity
  const getViolationSeverity = (type) => {
    const severityMap = {
      'TAB_SWITCH': 'high',
      'COPY_PASTE': 'high',
      'FACE_NOT_DETECTED': 'medium',
      'FULLSCREEN_EXIT': 'medium',
      'RAPID_MOUSE_MOVEMENTS': 'low',
      'UNUSUAL_TYPING_SPEED': 'low',
      'HIGH_AUDIO_LEVEL': 'low'
    };
    return severityMap[type] || 'low';
  };

  // Upload recording to Firebase Storage
  const uploadRecording = async (blob, type) => {
    // Implementation for uploading to Firebase Storage
    // This would integrate with your existing storage.js
  };

  // Stop proctoring session
  const stopProctoring = useCallback(async () => {
    setIsProctoringActive(false);
    
    // Stop all streams
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
    }
    if (screenRecording) {
      screenRecording.getTracks().forEach(track => track.stop());
    }
    
    // Stop recording
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    
    // Clear intervals
    if (faceDetectionInterval.current) {
      clearInterval(faceDetectionInterval.current);
    }
    if (behavioralTrackingInterval.current) {
      clearInterval(behavioralTrackingInterval.current);
    }
    
    // Update session status
    if (proctoringSession) {
      await updateDoc(doc(db, 'proctoringSessions', proctoringSession.sessionId), {
        status: 'completed',
        endTime: new Date(),
        finalViolations: violations
      });
    }
  }, [videoStream, audioStream, screenRecording, proctoringSession, violations]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopProctoring();
    };
  }, [stopProctoring]);

  const value = {
    isProctoringActive,
    proctoringSession,
    violations,
    isRecording,
    faceDetected,
    multipleFacesDetected,
    behavioralData,
    initializeProctoring,
    stopProctoring,
    addViolation
  };

  return (
    <ProctoringContext.Provider value={value}>
      {children}
      {/* Hidden video and canvas elements for face detection */}
      <video
        ref={videoRef}
        style={{ display: 'none' }}
        autoPlay
        muted
        playsInline
      />
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </ProctoringContext.Provider>
  );
}
